/**
 * The public hub's colours, derived from the org's brand colour.
 *
 * ── Why this exists ─────────────────────────────────────────────────
 *
 * /hub/{slug} once painted its header in the raw `primary_colour` with
 * white text on top and nobody checking the pair, so the redesign
 * dropped the org colour and drew every hub in TrailsIQ forest and
 * amber. That fixed the contrast and cost the customer their identity
 * on the one page their own visitors see.
 *
 * This brings the colour back WITHOUT the unchecked pair: every value
 * that carries text is derived from the brand colour and solved for
 * WCAG AA against the ground it actually sits on. An operator picks
 * one colour; nobody is asked to pick an ink for it.
 *
 * ── The values ──────────────────────────────────────────────────────
 *
 * `head`      header ground — `orgColorSet().btn`, the brand colour
 *             darkened only as far as a white label needs (never more
 *             than a third, after which a light colour keeps dark ink).
 * `headInk`   the name and headline on it — white or near-black.
 * `headSoft`  secondary lines on it (subtitle, "Updated", tile labels).
 *             The forest header wrote these at 62% white, which works
 *             only because forest has 15:1 of headroom. A brand colour
 *             darkened to just clear 4.5 has none, so a translucent ink
 *             fails; this is the head colour walked toward the ink only
 *             until it clears AA, the softest line that still reads.
 * `detail`    the date eyebrow and the initials tile — the brand hue,
 *             walked toward the header ink until it clears AA on the
 *             header, so it reads as a lighter (or darker) shade of
 *             the same colour rather than as a second brand.
 * `accent`    links, the active weather tab's underline, the search
 *             focus ring — `orgColorSet().ink`, the brand hue solved
 *             for AA on a light ground.
 * `tint` / `tintLine` / `tintInk`  the weather verdict pill: a wash of
 *             the brand colour with its ink re-solved against the wash.
 *
 * No colour → null, and the page keeps TrailsIQ's own palette exactly.
 */

import { AA_TEXT, contrast, inkOn, parseHex, readableOn, toHex, walkToward } from './contrast.js';
import { MAX_BUTTON_DARKEN, orgColorSet } from './orgColor.js';

/**
 * How much contrast the header aims to have with a white label.
 *
 * Not 4.5. A header darkened only until white JUST clears AA leaves
 * nothing between it and white that also clears — so the date eyebrow
 * could only ever be pure white, and the "detail" shade of the brand
 * colour the header is supposed to carry has nowhere to live. 6:1
 * leaves room for a pale tint of the same hue at 4.5. Measured: Stowe's
 * orange #e45419 lands at #ab3f13 with a #faddd1 date, a green #2f8a55
 * at #266e44 with #cbe2d5.
 */
export const HEAD_TARGET = 6;

const mix = (hex, target, t) => {
    const a = parseHex(hex);
    const b = parseHex(target);

    return toHex([0, 1, 2].map((i) => a[i] + (b[i] - a[i]) * t));
};

export const hubPalette = (colour) => {
    if (!parseHex(colour)) return null;
    const set = orgColorSet(colour);
    const rgb = parseHex(set.primary);

    // Darken toward HEAD_TARGET, never past the same cap the buttons
    // use (beyond it a colour stops being the one somebody picked). If
    // white cannot reach AA inside the cap the brand is a light one and
    // the header keeps it as-is with dark ink, same rule as a button.
    let head = null;
    for (let i = 0; i <= 40 && i / 40 <= MAX_BUTTON_DARKEN; i++) {
        const t = i / 40;
        const step = toHex(rgb.map((v) => v * (1 - t)));
        if (contrast('#ffffff', step) >= AA_TEXT) head = step;
        if (contrast('#ffffff', step) >= HEAD_TARGET) break;
    }
    const headInk = head ? '#ffffff' : inkOn(set.primary);
    head = head || set.primary;
    const light = headInk === '#ffffff';

    const tint = mix(set.primary, '#ffffff', 0.9);

    return {
        head,
        headInk,
        headSoft: walkToward(head, headInk, head, AA_TEXT),
        detail: walkToward(set.primary, headInk, head, AA_TEXT),
        // Translucent washes read on either ink; only their tone flips.
        headVeil: light ? 'rgba(255, 255, 255, .08)' : 'rgba(0, 0, 0, .06)',
        headLine: light ? 'rgba(255, 255, 255, .18)' : 'rgba(0, 0, 0, .16)',
        headHover: toHex(parseHex(head).map((v) => v + (light ? 22 : -22))),
        accent: set.ink,
        accentHover: mix(set.ink, '#000000', 0.25),
        tint,
        tintLine: mix(set.primary, '#ffffff', 0.7),
        tintInk: readableOn(set.ink, tint, AA_TEXT),
    };
};

/** The palette as the CSS custom properties DailyReportShow.vue reads. */
export const hubPaletteVars = (colour) => {
    const p = hubPalette(colour);
    if (!p) return {};

    return {
        '--hub-head': p.head,
        '--hub-head-ink': p.headInk,
        '--hub-head-soft': p.headSoft,
        '--hub-head-veil': p.headVeil,
        '--hub-head-line': p.headLine,
        '--hub-head-hover': p.headHover,
        '--hub-detail': p.detail,
        '--hub-accent': p.accent,
        '--hub-accent-hover': p.accentHover,
        '--hub-tint': p.tint,
        '--hub-tint-line': p.tintLine,
        '--hub-tint-ink': p.tintInk,
    };
};

